"use client";

import { motion } from "framer-motion";
import Image from "next/image";
import { useTranslations } from "next-intl";
import { PortfolioItem } from "@/lib/portfolio";
import {
  ArrowUpRight,
  Monitor,
  Gamepad2,
  Puzzle,
  Code,
  Smartphone,
} from "lucide-react";

interface ProjectCardProps {
  item: PortfolioItem;
}

// Icon mapping based on legacy site
const CategoryIcon = ({ category }: { category: string }) => {
  switch (category) {
    case "websites":
      return <Monitor className="w-4 h-4" />;
    case "games":
      return <Gamepad2 className="w-4 h-4" />;
    case "applications":
      return <Smartphone className="w-4 h-4" />;
    case "addons":
      return <Puzzle className="w-4 h-4" />;
    default:
      return <Code className="w-4 h-4" />;
  }
};

export function ProjectCard({ item }: ProjectCardProps) {
  const tPortfolio = useTranslations("Portfolio");

  return (
    <motion.div
      className="group relative w-full aspect-4/3 rounded-2xl overflow-hidden bg-(--bg-card) border border-(--border-subtle)"
      whileHover={{ y: -8 }}
      transition={{ type: "spring", stiffness: 300, damping: 20 }}
    >
      {/* Image Layer */}
      <div className="absolute inset-0 w-full h-full">
        <Image
          src={item.image}
          alt={item.title}
          fill
          className="object-cover transition-transform duration-700 ease-premium group-hover:scale-110 grayscale group-hover:grayscale-0"
        />
        <div className="absolute inset-0 bg-black/40 group-hover:bg-black/20 transition-colors duration-500" />
      </div>

      {/* Content Overlay (Glassmorphism) */}
      <div className="absolute inset-x-0 bottom-0 p-6 flex flex-col justify-end bg-linear-to-t from-black/90 via-black/50 to-transparent pt-20 translate-y-4 group-hover:translate-y-0 transition-transform duration-500 ease-premium">
        {/* Title & Category with Icon */}
        <div className="relative z-10 flex items-center justify-between">
          <div className="flex flex-col gap-1">
            <div className="flex items-center gap-2.5 text-(--brand-primary) text-xs font-bold uppercase tracking-wider">
              <CategoryIcon category={item.category[0]} />
              <span>{tPortfolio(`types.${item.type}`)}</span>
            </div>
            <h3 className="text-2xl font-bold font-outfit text-white leading-tight">
              {item.title}
            </h3>
          </div>

          {/* Action Icon */}
          <div className="w-10 h-10 rounded-full bg-white/10 backdrop-blur-md border border-white/20 flex items-center justify-center text-white opacity-0 group-hover:opacity-100 scale-75 group-hover:scale-100 transition-all duration-300 delay-100">
            <ArrowUpRight className="w-5 h-5" />
          </div>
        </div>
      </div>

      {/* Link Overlay */}
      {item.link && (
        <a
          href={item.link}
          className="absolute inset-0 z-20"
          aria-label={`${tPortfolio("labels.view_project")} ${item.title}`}
        />
      )}
    </motion.div>
  );
}
